import api from './api';

/**
 * #444: what an insurer says it paid a broker, one row per broker-month.
 * The insurer records them (`insurer-settlements/`); the broker reads and
 * answers them (`insurer-settlements-received/`). Neither touches the broker's
 * books. Amounts are API decimal strings.
 */

export type SettlementStatus = 'recorded' | 'acknowledged' | 'disputed' | 'voided';
export type SettlementMode = 'bank_transfer' | 'cheque' | 'upi' | 'other';

export const SETTLEMENT_MODE_OPTIONS: { value: SettlementMode; label: string }[] = [
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'upi', label: 'UPI' },
  { value: 'other', label: 'Other' },
];

export const SETTLEMENT_STATUS_LABEL: Record<SettlementStatus, string> = {
  recorded: 'Recorded',
  acknowledged: 'Acknowledged',
  disputed: 'Disputed',
  voided: 'Voided',
};

/** The insurer's view of a row. */
export interface InsurerSettlement {
  id: string;
  broker: string;
  broker_name: string;
  /** First day of the month the money is for (YYYY-MM-01). */
  period: string;
  amount: string;
  paid_on: string;
  reference: string;
  mode: SettlementMode;
  note: string;
  status: SettlementStatus;
  recorded_by_name: string;
  recorded_at: string | null;
  editable: boolean;
  edit_deadline: string | null;
  ack_at: string | null;
  disputed_at: string | null;
  disputed_amount: string | null;
  dispute_reason: string;
  voided_at: string | null;
  void_reason: string;
  supersedes: string | null;
  superseded_by: string | null;
}

/** The broker's view: the allowlisted fields only. */
export interface ReceivedSettlement {
  id: string;
  insurer_name: string;
  period: string;
  amount: string;
  paid_on: string;
  reference: string;
  mode: SettlementMode;
  note: string;
  status: SettlementStatus;
  ack_at: string | null;
  disputed_at: string | null;
  disputed_amount: string | null;
  dispute_reason: string;
  voided_at: string | null;
  supersedes: string | null;
}

export interface RecordSettlementInput {
  broker: string;
  /** YYYY-MM */
  period: string;
  amount: string;
  paid_on: string;
  reference: string;
  mode: SettlementMode;
  note?: string;
  supersedes?: string;
  /** #360: confirm adding a payment to a month that is already approved and fully paid. */
  confirm_closed_month?: boolean;
}

export interface SettlementListParams {
  broker?: string;
  period?: string;
  status?: SettlementStatus | '';
  insurer?: string;
}

interface Listing<T> {
  count: number;
  results: T[];
}

const clean = (params: SettlementListParams = {}) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v));

export const insurerSettlementsService = {
  async list(params?: SettlementListParams): Promise<Listing<InsurerSettlement>> {
    return (await api.get('/commissions/insurer-settlements/', { params: clean(params) })).data;
  },
  async record(input: RecordSettlementInput): Promise<InsurerSettlement> {
    return (await api.post('/commissions/insurer-settlements/', input)).data;
  },
  async edit(
    id: string,
    input: Partial<Pick<RecordSettlementInput, 'paid_on' | 'reference' | 'mode' | 'note'> & { amount: string }>
  ): Promise<InsurerSettlement> {
    return (await api.patch(`/commissions/insurer-settlements/${id}/`, input)).data;
  },
  async void(id: string, reason: string): Promise<InsurerSettlement> {
    return (await api.post(`/commissions/insurer-settlements/${id}/void/`, { reason })).data;
  },

  async listReceived(params?: SettlementListParams): Promise<Listing<ReceivedSettlement>> {
    return (await api.get('/commissions/insurer-settlements-received/', { params: clean(params) })).data;
  },
  async acknowledge(id: string): Promise<ReceivedSettlement> {
    return (await api.post(`/commissions/insurer-settlements-received/${id}/acknowledge/`)).data;
  },
  async dispute(id: string, receivedAmount: string, reason: string): Promise<ReceivedSettlement> {
    return (
      await api.post(`/commissions/insurer-settlements-received/${id}/dispute/`, {
        received_amount: receivedAmount,
        reason,
      })
    ).data;
  },
};

/** "2026-09-01" -> "Sep 2026" without a timezone shift. */
export function formatPeriod(period: string): string {
  const [y, m] = period.split('-').map(Number);
  if (!y || !m) return period;
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}
